import React, { createContext, useContext, useState } from "react";
import axios from "axios";

const PaymentVoucherContext = createContext();

export const usePaymentVoucherContext = () => {
  const context = useContext(PaymentVoucherContext);
  if (!context) {
    throw new Error(
      "usePaymentVoucherContext must be used within a PaymentVoucherProvider"
    );
  }
  return context;
};

export const PaymentVoucherProvider = ({ children }) => {
  const [paymentVouchers, setPaymentVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // جلب جميع سندات الصرف
  const fetchPaymentVouchers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/paymentVoucher/getPaymentVouchers.php`,
        {
          withCredentials: true,
        }
      );
      if (response.data.status === "success") {
        setPaymentVouchers(response.data.vouchers);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError("فشل في جلب سندات الصرف");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // جلب سند صرف واحد
  const fetchPaymentVoucher = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/paymentVoucher/getPaymentVoucher.php?id=${id}`,
        {
          withCredentials: true,
        }
      );
      if (response.data.status === "success") {
        return response.data.voucher;
      } else {
        setError(response.data.message);
        return null;
      }
    } catch (err) {
      setError("فشل في جلب سند الصرف");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // جلب فواتير المورد
  const fetchSupplierInvoices = async (supplierId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/paymentVoucher/getSupplierInvoices.php?supplier_id=${supplierId}`,
        {
          withCredentials: true,
        }
      );
      if (response.data.status === "success") {
        return response.data.invoices;
      } else {
        setError(response.data.message);
        return [];
      }
    } catch (err) {
      setError("فشل في جلب فواتير المورد");
      console.error(err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // إنشاء سند صرف جديد
  const createPaymentVoucher = async (voucherData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/paymentVoucher/newPaymentVoucher.php`,
        voucherData,
        {
          withCredentials: true,
        }
      );
      if (response.data.status === "success") {
        setSuccess(true);
        return response.data;
      } else {
        setError(response.data.message);
        throw new Error(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "فشل في إنشاء سند الصرف");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // جلب سندات الصرف لفاتورة معينة
  const fetchInvoicePaymentVouchers = async (invoiceId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/paymentVoucher/getInvoicePaymentVouchers.php?invoice_id=${invoiceId}`,
        {
          withCredentials: true,
        }
      );
      if (response.data.status === "success") {
        return response.data.vouchers;
      } else {
        setError(response.data.message);
        return [];
      }
    } catch (err) {
      setError("فشل في جلب سندات الصرف");
      console.error(err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // حذف سند صرف
  const deletePaymentVoucher = async (voucherId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/paymentVoucher/deletePaymentVoucher.php`,
        { voucher_id: voucherId },
        {
          withCredentials: true,
        }
      );
      if (response.data.status === "success") {
        return response.data;
      } else {
        setError(response.data.message);
        throw new Error(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "فشل في حذف سند الصرف");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    paymentVouchers,
    loading,
    error,
    success,
    fetchPaymentVouchers,
    fetchPaymentVoucher,
    fetchSupplierInvoices,
    createPaymentVoucher,
    fetchInvoicePaymentVouchers,
    deletePaymentVoucher,
  };

  return (
    <PaymentVoucherContext.Provider value={value}>
      {children}
    </PaymentVoucherContext.Provider>
  );
};
